import React from "react"

import { BrandLogo, PaymentLogos } from "@trueskin-web/components"
import { applePayIcon } from "@trueskin-web/theme/icons/applePayIcon"
import { formelIcon } from "@trueskin-web/theme/icons/formelIcon"
import { klarnaIcon } from "@trueskin-web/theme/icons/klarnaIcon"
import { paypalIcon } from "@trueskin-web/theme/icons/paypalIcon"
import { sepaIcon } from "@trueskin-web/theme/icons/sepaIcon"
import i18next from "@trueskin-web/translations/src/i18next"

import { Adyen } from "./components/adyen"
import { Klarna } from "./components/klarna"
import { Paypal } from "./components/paypal"
import {
  PAYMENT_METHODS,
  supportedPaymentMethodsOrder,
} from "./supported-payment-methods"

const i18nT = (type, resource) =>
  i18next.t(`Payment.Methods.${type}.${resource}`)

const supportedPaymentMethodsDescriptionVisibility = ["klarna", "applepay"]

const supportedPaymentMethods = [
  {
    type: PAYMENT_METHODS.APPLE_PAY,
    logo: ({ className }) => (
      <BrandLogo className={className} icon={applePayIcon} size="lg" />
    ),
    paymentMethod: ({ className, order }) => (
      <Adyen className={className} type="applepay" order={order} />
    ),
  },
  {
    type: PAYMENT_METHODS.CARD,
    logo: ({ className }) => (
      <PaymentLogos
        className={className}
        cardsOnly
        size="lg"
        sx={{
          justifyContent: "end",
        }}
      />
    ),
    paymentMethod: ({ className, order }) => (
      <Adyen className={className} type="card" order={order} />
    ),
  },
  {
    type: PAYMENT_METHODS.KLARNA,
    logo: ({ className }) => (
      <BrandLogo className={className} icon={klarnaIcon} size="lg" />
    ),
    paymentMethod: ({ order }) => <Klarna type="klarna" order={order} />,
  },
  {
    type: PAYMENT_METHODS.PAYPAL,
    logo: ({ className }) => (
      <BrandLogo className={className} icon={paypalIcon} size="lg" />
    ),
    paymentMethod: ({ order }) => <Paypal type="paypal" order={order} />,
  },
  {
    type: PAYMENT_METHODS.PARTNER_CARD,
    logo: ({ className }) => (
      <BrandLogo className={className} icon={formelIcon} size="lg" />
    ),
    paymentMethod: ({ JustPurchase }) => (
      <>{JustPurchase && <JustPurchase />}</>
    ),
  },
  {
    type: PAYMENT_METHODS.SEPA,
    logo: ({ className }) => (
      <BrandLogo className={className} icon={sepaIcon} size="lg" />
    ),
    paymentMethod: ({ order }) => (
      <Adyen type="sepadirectdebit" order={order} />
    ),
  },
].map((paymentMethod) => ({
  ...paymentMethod,
  get title() {
    return i18nT(paymentMethod.type, "title")
  },
  get description() {
    return i18nT(paymentMethod.type, "description")
  },
  get showDescription() {
    return supportedPaymentMethodsDescriptionVisibility.includes(
      paymentMethod.type
    )
  },
}))

const isPaymentMethodAvailable = ({ type, hidePaymentMethods = [] }) => {
  if (hidePaymentMethods?.includes(type)) {
    return false
  }

  try {
    switch (type) {
      case PAYMENT_METHODS.PARTNER_CARD:
        return true
      case PAYMENT_METHODS.CARD:
        return true
      case PAYMENT_METHODS.PAYPAL:
        return true
      case PAYMENT_METHODS.SEPA:
        return !!process.env.GATSBY_FEATURE_FLAG_DIRECT_DEBIT
      case PAYMENT_METHODS.APPLE_PAY:
        return !!(
          process.env.GATSBY_FEATURE_FLAG_APPLE_PAY &&
          window.ApplePaySession?.canMakePayments()
        )
      case PAYMENT_METHODS.KLARNA:
        return !!process.env.GATSBY_FEATURE_FLAG_KLARNA
      default:
        return false
    }
  } catch {
    // localhost: Trying to start an Apple Pay session from an insecure document.
    return false
  }
}

export const usePaymentMethods = ({ hidePaymentMethods }) => {
  const supportedPaymentMethodsAvailability = supportedPaymentMethodsOrder.map(
    (type) => isPaymentMethodAvailable({ type, hidePaymentMethods })
  )

  const paymentMethods = supportedPaymentMethodsOrder.reduce(
    (acc, type, index) => {
      if (supportedPaymentMethodsAvailability[index]) {
        return [
          ...acc,
          supportedPaymentMethods.find(
            ({ type: paymentType }) => paymentType === type
          ),
        ]
      }

      return acc
    },
    []
  )

  return React.useMemo(() => paymentMethods, [paymentMethods])
}
